/**
 * M2 P7 / M3 WORLD B8: save/load. Pure, deterministic serialize/deserialize of a
 * versioned SaveState (no Three.js, no IO — the client owns localStorage).
 * deserializeSave is defensive: it returns null on malformed JSON or a core shape
 * mismatch so a corrupt save can never crash the boot path.
 *
 * B8 v2: bumps SAVE_VERSION to 2 and adds discoveredSignalIds / objectives /
 * activeObjectiveId. A v1 save is MIGRATED (the new fields default to empty),
 * never rejected, so existing players keep their progress. Live signal positions
 * are NOT saved — they are re-seeded deterministically from the player's quantized
 * position (signal.ts), so only the discovered IDS persist.
 */
import type { Vec3 } from './vec3.js';
import type { Quat } from './quat.js';
import type { CargoItem } from './economy.js';
import type { Objective } from './objective.js';
import type { VerifyStatus } from './verification.js';
import { FACTION_IDS, neutralFactionStanding, type FactionStanding } from './faction.js';

export const SAVE_VERSION = 5;

/** v4: persisted campaign progress (wave 1). null = no campaign yet (or a
 * pre-campaign save) — the client seeds a fresh/skip-cold-open state. */
export interface SavedCampaign {
  act: number;
  beatId: string;
  flags: Record<string, string | boolean>;
}

/** v3: a persisted Verification Ledger row (revealChars is presentation-only —
 * the client restores rows fully revealed). */
export interface SavedLedgerRow {
  name: string;
  status: VerifyStatus;
}

export interface SaveState {
  version: number;
  shipId: string;
  credits: number;
  cargo: CargoItem[];
  activeMissionId: string | null;
  pos: Vec3;
  orientation: Quat;
  // B8 v2 additions (a v1 save migrates these to empty defaults).
  discoveredSignalIds: string[];
  objectives: Objective[];
  activeObjectiveId: string | null;
  // AUDIT v3 (session 6): the carved record is PERMANENT — verified signal ids
  // and the Verification Ledger persist across sessions (this game's thesis is
  // the ledger; re-verifying the same name after a reload betrayed it).
  // v1/v2 saves migrate both to empty.
  verifiedIds: string[];
  ledger: SavedLedgerRow[];
  // CAMPAIGN W1 (v4): act/beat/flags. v1-v3 saves migrate to null.
  campaign: SavedCampaign | null;
  // SHIP-EXPANSION (v5): the player's hangar. ownedShips lists every hull the
  // player owns (the active hull is `shipId`, which is always one of these);
  // factionStanding is per-faction reputation (neutral default); shipUnlocks are
  // story/derelict unlock tokens (purchases don't use these). v1-v4 saves migrate:
  // ownedShips seeds from the prior shipId then force-adds Vesper-3, factionStanding
  // becomes neutral, shipUnlocks empty.
  ownedShips: string[];
  factionStanding: FactionStanding;
  shipUnlocks: string[];
}

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

const isVec3 = (v: unknown): v is Vec3 => {
  const o = v as Record<string, unknown>;
  return !!o && isNum(o.x) && isNum(o.y) && isNum(o.z);
};

const isQuat = (v: unknown): v is Quat => {
  const o = v as Record<string, unknown>;
  return !!o && isNum(o.w) && isNum(o.x) && isNum(o.y) && isNum(o.z);
};

const isCargo = (v: unknown): v is CargoItem[] =>
  Array.isArray(v) &&
  v.every((it) => {
    const o = it as Record<string, unknown>;
    return !!o && typeof o.commodityId === 'string' && isNum(o.qty);
  });

/** v2: a string-id array (discovered signal ids). Absent/invalid => []. */
const asStringIds = (v: unknown): string[] =>
  Array.isArray(v) && v.every((x) => typeof x === 'string') ? (v as string[]) : [];

/** v2: shape-check a stored Objective array; anything malformed migrates to []. */
const asObjectives = (v: unknown): Objective[] => {
  if (!Array.isArray(v)) return [];
  const ok = v.every((it) => {
    const o = it as Record<string, unknown>;
    return (
      !!o &&
      typeof o.id === 'string' &&
      typeof o.kind === 'string' &&
      typeof o.status === 'string' &&
      isNum(o.progress) &&
      isNum(o.goal) &&
      isNum(o.reward)
    );
  });
  return ok ? (v as Objective[]) : [];
};

/** v3: shape-check stored ledger rows; anything malformed migrates to []. */
const asLedger = (v: unknown): SavedLedgerRow[] => {
  if (!Array.isArray(v)) return [];
  const ok = v.every((it) => {
    const o = it as Record<string, unknown>;
    return !!o && typeof o.name === 'string' && typeof o.status === 'string';
  });
  return ok ? (v as SavedLedgerRow[]) : [];
};

/** v4: shape-check stored campaign progress; anything malformed migrates to null. */
const asCampaign = (v: unknown): SavedCampaign | null => {
  const o = v as Record<string, unknown>;
  if (!o || typeof o !== 'object') return null;
  if (!isNum(o.act) || typeof o.beatId !== 'string') return null;
  const flags = o.flags as Record<string, unknown>;
  if (!flags || typeof flags !== 'object') return null;
  const flagsOk = Object.values(flags).every(
    (x) => typeof x === 'string' || typeof x === 'boolean',
  );
  if (!flagsOk) return null;
  return { act: o.act, beatId: o.beatId, flags: flags as Record<string, string | boolean> };
};

/** v5: shape-check stored faction standing; absent/malformed entries fall back to
 * neutral (0). Always returns a full record covering every faction. */
const asFactionStanding = (v: unknown): FactionStanding => {
  const out = neutralFactionStanding();
  if (!v || typeof v !== 'object') return out;
  const o = v as Record<string, unknown>;
  for (const f of FACTION_IDS) if (isNum(o[f])) out[f] = o[f] as number;
  return out;
};

/** Order-preserving de-duplication of a string-id list. */
const dedupe = (ids: string[]): string[] => [...new Set(ids)];

/** v5 vesper3-forcing: the hull every in-progress game gains + flies on migration. */
const FORCED_SHIP_ID = 'vesper3';

/**
 * v5 migration: resolve the active shipId + ownedShips. A pre-v5 save (version < 5)
 * is forced onto Vesper-3 — the prior hull is PRESERVED in ownedShips, Vesper-3 is
 * added (deduplicated), and it becomes the active hull. A v5 save preserves its
 * persisted ownedShips + active shipId verbatim.
 */
const resolveOwnership = (
  version: number,
  priorShipId: string,
  rawOwned: unknown,
): { shipId: string; ownedShips: string[] } => {
  const stored = asStringIds(rawOwned);
  const base = stored.length > 0 ? dedupe(stored) : [priorShipId];
  if (version < 5) {
    return { shipId: FORCED_SHIP_ID, ownedShips: dedupe([...base, FORCED_SHIP_ID]) };
  }
  return { shipId: priorShipId, ownedShips: base };
};

/** Serialize a save to a JSON string. */
export const serializeSave = (s: SaveState): string => JSON.stringify(s);

/**
 * Parse + validate a save string. Returns null only on malformed JSON or a broken
 * CORE shape. A v1 save (no version-2 fields) is MIGRATED, not rejected: the new
 * discoveredSignalIds / objectives / activeObjectiveId default to empty. Any
 * version <= SAVE_VERSION is accepted and upgraded.
 */
export const deserializeSave = (json: string): SaveState | null => {
  let p: unknown;
  try {
    p = JSON.parse(json);
  } catch {
    return null;
  }
  const o = p as Record<string, unknown>;
  if (!o || !isNum(o.version) || o.version > SAVE_VERSION) return null; // future saves rejected
  if (typeof o.shipId !== 'string' || !isNum(o.credits)) return null;
  if (!isCargo(o.cargo) || !isVec3(o.pos) || !isQuat(o.orientation)) return null;
  if (o.activeMissionId !== null && typeof o.activeMissionId !== 'string') return null;
  const activeObjectiveId =
    typeof o.activeObjectiveId === 'string' ? o.activeObjectiveId : null;
  // v5: hangar. A pre-v5 save is force-migrated onto Vesper-3 (prior hull kept).
  const { shipId, ownedShips } = resolveOwnership(o.version, o.shipId, o.ownedShips);
  return {
    version: SAVE_VERSION, // always upgrade to current
    shipId,
    credits: o.credits,
    cargo: o.cargo,
    activeMissionId: o.activeMissionId,
    pos: o.pos,
    orientation: o.orientation,
    discoveredSignalIds: asStringIds(o.discoveredSignalIds), // v1 -> []
    objectives: asObjectives(o.objectives), // v1 -> []
    activeObjectiveId, // v1 -> null
    verifiedIds: asStringIds(o.verifiedIds), // v1/v2 -> []
    ledger: asLedger(o.ledger), // v1/v2 -> []
    campaign: asCampaign(o.campaign), // v1-v3 -> null
    ownedShips, // v1-v4 -> [priorShip, 'vesper3']; v5 -> persisted
    factionStanding: asFactionStanding(o.factionStanding), // v1-v4 -> neutral
    shipUnlocks: asStringIds(o.shipUnlocks), // v1-v4 -> []
  };
};
